import { milis } from "@shared/helpers/duration";

export interface OTPConfig {
  /**
   * (in milliseconds) The time after which the code expires
   */
  codeExpiresAfter: number;

  /**
   * (in milliseconds) The minimum time between two code requests
   ** time resets after a successful code login
   */
  minNewCodeRequestTimeout: number;

  /**
   * ```ts
   * {
   *  [BLOCK_NAME]: {
   *    tries: number, // the minimum number of wrong attempts to activate this block
   *    time: number // the time the user will be blocked in milliseconds
   *  }
   * }
   * ```
   */
  blockOptions: Record<string, Record<"tries" | "time", number>>;

  /**
   * (in milliseconds) The time after which the login session is considered a new login session (must be larger than the longest block)
   */
  maxValidityTime: number;

  /**
   * If `false`, block and min time between code requests will be ignored in development
   */
  blockUserInDev: boolean;
}

export const OTP_CONFIG = {
  codeExpiresAfter: milis(5, "minutes"),
  minNewCodeRequestTimeout: milis(1, "minute"),
  blockOptions: {
    NO_BLOCK: {
      tries: 0,
      time: 0,
    },
    SHORT_BLOCK: {
      tries: 5,
      time: milis(5, "minutes"),
    },
    LONG_BLOCK: {
      tries: 10,
      time: milis(1, "hour"),
    },
    DAY_BLOCK: {
      tries: 15,
      time: milis(1, "day"),
    },
  },
  maxValidityTime: milis(5, "days"),
  /** In development (MODE=development): when false, skip "wait before another code" and block – for easier testing */
  blockUserInDev: false,
} satisfies OTPConfig;

export const BLOCK_KEYS = Object.keys(
  OTP_CONFIG.blockOptions,
) as (keyof typeof OTP_CONFIG.blockOptions)[];

export type Block = (typeof BLOCK_KEYS)[number];

export function getBlock(wrongAttempts: number) {
  for (const [key, value] of Object.entries(OTP_CONFIG.blockOptions)) {
    if (wrongAttempts === value.tries) return key as Block;
  }
  return "NO_BLOCK" as Block;
}
